import React from "react";
import { ArrowUpRight, CalendarDays, Clock3, MapPin } from "lucide-react";

export type Course = {
  id: string;
  apoloScheduleId?: number;
  title: string;
  location: string;
  category: string;
  shortDescription: string;
  startDate: string;
  duration: string;
  imageUrl?: string;
  pageContent?: string;
  registrationEmail?: string;
  hidden?: boolean;
};

export type CourseScheduleStatus = "upcoming" | "ongoing" | "finished" | "unknown";

export function getCourseScheduleInfo(value: string) {
  const matches = Array.from(value.matchAll(/(\d{1,2})\/(\d{1,2})\/(\d{4})/g));
  const toDate = (match?: RegExpMatchArray) =>
    match ? new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1])) : null;
  const start = toDate(matches[0]);
  const end = toDate(matches[1]);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let status: CourseScheduleStatus = "unknown";
  if (end && end < today) status = "finished";
  else if (start && start > today) status = "upcoming";
  else if (start && start <= today && (!end || end >= today)) status = "ongoing";

  const enrollmentDeadline = start ? new Date(start) : null;
  enrollmentDeadline?.setDate(enrollmentDeadline.getDate() + 5);
  const startsThisMonth = Boolean(
    start && start.getFullYear() === today.getFullYear() && start.getMonth() === today.getMonth(),
  );
  const enrollmentOpen =
    (status === "upcoming" && startsThisMonth) ||
    (status === "ongoing" && Boolean(enrollmentDeadline && today <= enrollmentDeadline));
  const isUnavailable = status === "finished" || (status === "ongoing" && !enrollmentOpen);

  return { start, end, status, enrollmentDeadline, startsThisMonth, enrollmentOpen, isUnavailable };
}

export function sortCoursesByAvailability(courses: Course[]) {
  return [...courses].sort((a, b) => {
    const aSchedule = getCourseScheduleInfo(a.startDate);
    const bSchedule = getCourseScheduleInfo(b.startDate);
    const priority = (schedule: ReturnType<typeof getCourseScheduleInfo>) => {
      if (schedule.enrollmentOpen || schedule.status === "upcoming") return 0;
      if (schedule.status === "ongoing") return 1;
      if (schedule.status === "unknown") return 2;
      return 3;
    };
    const priorityDifference = priority(aSchedule) - priority(bSchedule);
    if (priorityDifference) return priorityDifference;

    const startDifference =
      (aSchedule.start?.getTime() ?? Number.POSITIVE_INFINITY) -
      (bSchedule.start?.getTime() ?? Number.POSITIVE_INFINITY);
    return startDifference || a.title.localeCompare(b.title, "es");
  });
}

export default function CourseCard({ course, onRegister }: { course: Course; onRegister: (c: Course) => void }) {
  const { status, enrollmentOpen, isUnavailable } = getCourseScheduleInfo(course.startDate);
  const statusLabel =
    enrollmentOpen
      ? "Inscripción abierta"
      : status === "finished"
      ? "Finalizado"
      : status === "ongoing" && !enrollmentOpen
        ? "No disponible"
        : status === "upcoming"
          ? "Próximamente"
          : null;
  const imageUrl = course.imageUrl
    ? encodeURI(course.imageUrl)
    : "https://images.pexels.com/photos/3184296/pexels-photo-3184296.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1";

  return (
    <article className="card group flex h-full cursor-pointer flex-col overflow-hidden p-0" onClick={() => onRegister(course)}>
      <div className="relative h-44 overflow-hidden bg-teal-950">
        <img
          src={imageUrl}
          alt={course.title}
          className={`h-full w-full object-cover transition duration-500 group-hover:scale-105 ${
            isUnavailable ? "grayscale blur-[1px] opacity-60" : ""
          }`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
        <span className="absolute left-4 top-4 rounded-full bg-white px-3 py-1 text-xs font-bold text-[var(--primary)] shadow-sm">
          {course.category}
        </span>
        {statusLabel && (
          <span
            className={`absolute right-4 top-4 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide shadow-sm ${
              isUnavailable ? "bg-slate-800 text-white" : "bg-white text-[var(--ink)]"
            }`}
          >
            {statusLabel}
          </span>
        )}
      </div>

      <div className="flex flex-1 flex-col p-5">
        <div className="mb-4 h-1.5 w-16 rounded-full bg-[var(--accent)]" />
        <h3 className="text-xl font-black leading-tight text-[var(--ink)] group-hover:text-[var(--primary)]">
          {course.title}
        </h3>
        <p className="mt-3 flex-1 text-sm leading-6 text-slate-600">{course.shortDescription}</p>

        <div className="mt-5 grid gap-2 rounded-md bg-[var(--soft)] p-3 text-sm text-slate-700">
          <p className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-teal-700" />
            {course.startDate}
          </p>
          <p className="flex items-center gap-2">
            <Clock3 className="h-4 w-4 text-teal-700" />
            {course.duration}
          </p>
          <p className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-teal-700" />
            {course.location}
          </p>
        </div>

        <button
          className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[var(--ink)] px-4 py-3 text-sm font-bold text-white transition hover:bg-[var(--primary)]"
          onClick={(e) => {
            e.stopPropagation();
            onRegister(course);
          }}
        >
          {isUnavailable ? "Avísame cuando vuelva" : "Me interesa"} <ArrowUpRight className="h-4 w-4" />
        </button>
      </div>
    </article>
  );
}
